/**
 * date.util.ts
 *
 * Shared date helpers for the app's "MM/DD/YYYY hh:mm A" display format
 * (used by app-date-picker) and the "YYYY-MM-DD HH:mm:ss.SSS" ISO-like
 * format expected by backend payloads.
 */

/**
 * Converts a JS Date into "MM/DD/YYYY hh:mm A" (12-hour clock with AM/PM).
 * e.g. new Date(2026, 7, 4, 15, 45) -> "08/04/2026 03:45 PM"
 */
export function formatDateToMDYWithTime(value: Date): string {
  const mm = String(value.getMonth() + 1).padStart(2, '0');
  const dd = String(value.getDate()).padStart(2, '0');
  const yyyy = value.getFullYear();

  let hours = value.getHours();
  const minutes = String(value.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hh = String(hours).padStart(2, '0');

  return `${mm}/${dd}/${yyyy} ${hh}:${minutes} ${ampm}`;
}

/**
 * Converts a "MM/DD/YYYY hh:mm A" string (as produced by
 * formatDateToMDYWithTime / app-date-picker) into
 * "YYYY-MM-DD HH:mm:ss.SSS" (24-hour clock, seconds/ms zeroed).
 *
 * Returns null if dateStr is falsy or malformed.
 * e.g. "08/04/2026 03:45 PM" -> "2026-08-04 15:45:00.000"
 */
export function formatMDYWithTimeToISO(dateStr: string | null): string | null {
  if (!dateStr) return null;

  const [datePart, timePart, meridiem] = dateStr.trim().split(' ');
  if (!datePart) return null;

  const [month, day, year] = datePart.split('/');
  if (!month || !day || !year) return null;

  let hh = '00';
  let mm = '00';

  if (timePart) {
    const [hoursRaw, minutesRaw] = timePart.split(':').map((v) => parseInt(v, 10));
    let hours = hoursRaw;
    const minutes = minutesRaw;

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    hh = String(hours).padStart(2, '0');
    mm = String(minutes).padStart(2, '0');
  }

  const yyyy = year;
  const mo = month.padStart(2, '0');
  const dd = day.padStart(2, '0');

  return `${yyyy}-${mo}-${dd}T${hh}:${mm}:00`;
}